import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { criarAcao, type NovaAcao } from '@/features/acoes/api/acoesApi';
import {
  areasSesmtEmOrdem,
  modulosEmOrdem,
  rotuloDaAreaSesmt,
  rotuloDoModulo,
} from '@/features/acoes/rotulos';
import { useResolvedores } from '@/features/referencias/useReferencias';
import { descreverErro } from '@/lib/erros';
import type { AcaoDetalhada, AreaSesmt, ModuloDoSistema } from '@/types/api';

import estilos from './FormularioDeNovaAcao.module.css';

/**
 * Formulário de criação de ação. Especificação V3.1, item 7.
 *
 * A AÇÃO NASCE COMO RASCUNHO, e a tela não oferece alternativa. Enviar direto para
 * análise sem revisar é o atalho que produz solicitação incompleta na fila do Produto —
 * e o fluxo já tem um passo explícito para enviar, na própria tela da ação.
 *
 * AO MENOS UM CLIENTE é obrigatório. É a Decisão 11, garantida pelo domínio e por
 * `CK_Acao_TemCliente` no banco. O formulário valida antes de enviar para o usuário não
 * perder o que digitou num 400.
 *
 * NÃO SABE ONDE ESTÁ. Recebe `aoCriar` e `aoDesistir` e não navega: hoje vive numa
 * gaveta sobre a lista, e amarrar a navegação aqui dentro impediria reaproveitá-lo.
 * Invalidar o cache da lista também é de quem chama, porque quem fica na lista é ele.
 */

interface Props {
  aoCriar: (acao: AcaoDetalhada) => void;
  aoDesistir: () => void;
}

export function FormularioDeNovaAcao({ aoCriar, aoDesistir }: Props) {
  const { areasAtivas, clientesAtivos, carregando } = useResolvedores();

  const [assunto, definirAssunto] = useState('');
  const [descricao, definirDescricao] = useState('');
  const [planoAcao, definirPlanoAcao] = useState('');
  const [areaResponsavelId, definirAreaResponsavel] = useState<number | ''>('');
  const [areaSolicitanteId, definirAreaSolicitante] = useState<number | ''>('');
  const [areaSesmt, definirAreaSesmt] = useState<AreaSesmt | ''>('');
  const [modulo, definirModulo] = useState<ModuloDoSistema | ''>('');
  const [clienteIds, definirClientes] = useState<number[]>([]);

  const [tentouEnviar, definirTentouEnviar] = useState(false);
  const [falha, definirFalha] = useState<string | null>(null);

  const criar = useMutation({
    mutationFn: (nova: NovaAcao) => criarAcao(nova),
    onSuccess: (acao) => {
      definirFalha(null);
      aoCriar(acao);
    },
    onError: (erro) => {
      definirFalha(descreverErro(erro));
    },
  });

  const alternarCliente = (id: number) => {
    definirClientes((atuais) =>
      atuais.includes(id) ? atuais.filter((c) => c !== id) : [...atuais, id],
    );
  };

  const assuntoVazio = assunto.trim() === '';
  const semAreaResponsavel = areaResponsavelId === '';
  const semModulo = modulo === '';
  const semCliente = clienteIds.length === 0;

  const enviar = () => {
    definirTentouEnviar(true);

    if (assuntoVazio || semAreaResponsavel || semModulo || semCliente) {
      return;
    }

    criar.mutate({
      assunto: assunto.trim(),
      descricao: descricao.trim() === '' ? null : descricao.trim(),
      planoAcao: planoAcao.trim() === '' ? null : planoAcao.trim(),
      areaResponsavelId: Number(areaResponsavelId),
      areaSolicitanteId: areaSolicitanteId === '' ? null : Number(areaSolicitanteId),
      areaSesmt: areaSesmt === '' ? null : areaSesmt,
      modulo,
      clienteIds,
      comoRascunho: true,
    });
  };

  /*
    Sem área ou sem cliente cadastrado, o formulário não tem como funcionar: a área
    responsável é obrigatória e ao menos um cliente também. Dizer isso é mais útil do que
    mostrar seletores vazios e deixar a pessoa descobrir no 400.
  */
  const faltaCadastro = !carregando && (areasAtivas.length === 0 || clientesAtivos.length === 0);

  return (
    <form
      className={estilos.formulario}
      onSubmit={(evento) => {
        evento.preventDefault();
        enviar();
      }}
      noValidate
    >
      {faltaCadastro && (
        <p className={estilos.bloqueio} role="alert">
          {areasAtivas.length === 0 && clientesAtivos.length === 0
            ? 'Não há área nem cliente cadastrado. Os dois são obrigatórios para criar uma ação.'
            : areasAtivas.length === 0
              ? 'Não há área ativa cadastrada, e a área responsável é obrigatória.'
              : 'Não há cliente ativo cadastrado, e ao menos um cliente é obrigatório.'}{' '}
          Peça a um administrador para cadastrar antes.
        </p>
      )}

      {falha !== null && (
        <p className={estilos.falha} role="alert">
          {falha}
        </p>
      )}

      <section className={estilos.secao}>
        <h3 className={estilos.tituloDaSecao}>O que aconteceu</h3>

        <div className={estilos.campo}>
          <label className={estilos.rotulo} htmlFor="assunto">
            Assunto <span className={estilos.obrigatorio}>*</span>
          </label>
          <input
            id="assunto"
            className={
              tentouEnviar && assuntoVazio
                ? `${estilos.entrada} ${estilos.invalido}`
                : estilos.entrada
            }
            value={assunto}
            maxLength={200}
            aria-invalid={tentouEnviar && assuntoVazio}
            onChange={(evento) => {
              definirAssunto(evento.target.value);
            }}
          />
          {tentouEnviar && assuntoVazio ? (
            <span className={estilos.erro}>Informe o assunto.</span>
          ) : (
            <span className={estilos.ajuda}>
              Uma frase que identifique o problema. É o que aparece na lista.
            </span>
          )}
        </div>

        <div className={estilos.campo}>
          <label className={estilos.rotulo} htmlFor="descricao">
            Descrição
          </label>
          <textarea
            id="descricao"
            className={estilos.area}
            value={descricao}
            onChange={(evento) => {
              definirDescricao(evento.target.value);
            }}
          />
          <span className={estilos.ajuda}>
            O contexto que o Produto precisa para analisar. Pode ser completado depois, enquanto a
            ação estiver em rascunho.
          </span>
        </div>

        <div className={estilos.campo}>
          <label className={estilos.rotulo} htmlFor="planoAcao">
            Plano de ação
          </label>
          <textarea
            id="planoAcao"
            className={estilos.area}
            value={planoAcao}
            onChange={(evento) => {
              definirPlanoAcao(evento.target.value);
            }}
          />
          <span className={estilos.ajuda}>Opcional. O que se espera que seja feito.</span>
        </div>
      </section>

      <section className={estilos.secao}>
        <h3 className={estilos.tituloDaSecao}>Para quem vai</h3>

        <div className={estilos.dupla}>
          <div className={estilos.campo}>
            <label className={estilos.rotulo} htmlFor="areaResponsavel">
              Área responsável <span className={estilos.obrigatorio}>*</span>
            </label>
            <select
              id="areaResponsavel"
              className={
                tentouEnviar && semAreaResponsavel
                  ? `${estilos.selecao} ${estilos.invalido}`
                  : estilos.selecao
              }
              value={areaResponsavelId}
              aria-invalid={tentouEnviar && semAreaResponsavel}
              onChange={(evento) => {
                definirAreaResponsavel(
                  evento.target.value === '' ? '' : Number(evento.target.value),
                );
              }}
            >
              <option value="">Selecione</option>
              {areasAtivas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.nome}
                </option>
              ))}
            </select>
            {tentouEnviar && semAreaResponsavel ? (
              <span className={estilos.erro}>Escolha a área responsável.</span>
            ) : (
              <span className={estilos.ajuda}>Quem vai tratar a ação.</span>
            )}
          </div>

          <div className={estilos.campo}>
            <label className={estilos.rotulo} htmlFor="areaSolicitante">
              Área solicitante
            </label>
            <select
              id="areaSolicitante"
              className={estilos.selecao}
              value={areaSolicitanteId}
              onChange={(evento) => {
                definirAreaSolicitante(
                  evento.target.value === '' ? '' : Number(evento.target.value),
                );
              }}
            >
              <option value="">Nenhuma</option>
              {areasAtivas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.nome}
                </option>
              ))}
            </select>
            {/* Quem abriu a demanda acompanha o andamento mesmo depois de ela ser
                direcionada: o filtro de escopo inclui a área solicitante. */}
            <span className={estilos.ajuda}>
              Quem abriu a demanda. A área informada aqui continua vendo a ação depois.
            </span>
          </div>
        </div>

        <div className={estilos.dupla}>
          <div className={estilos.campo}>
            <label className={estilos.rotulo} htmlFor="areaSesmt">
              Área SESMT
            </label>
            <select
              id="areaSesmt"
              className={estilos.selecao}
              value={areaSesmt}
              onChange={(evento) => {
                definirAreaSesmt(evento.target.value as AreaSesmt | '');
              }}
            >
              <option value="">Nenhuma</option>
              {areasSesmtEmOrdem.map((opcao) => (
                <option key={opcao} value={opcao}>
                  {rotuloDaAreaSesmt[opcao]}
                </option>
              ))}
            </select>
            <span className={estilos.ajuda}>Opcional.</span>
          </div>

          <div className={estilos.campo}>
            <label className={estilos.rotulo} htmlFor="modulo">
              Módulo <span className={estilos.obrigatorio}>*</span>
            </label>
            <select
              id="modulo"
              className={
                tentouEnviar && semModulo
                  ? `${estilos.selecao} ${estilos.invalido}`
                  : estilos.selecao
              }
              value={modulo}
              aria-invalid={tentouEnviar && semModulo}
              onChange={(evento) => {
                definirModulo(evento.target.value as ModuloDoSistema | '');
              }}
            >
              <option value="">Selecione</option>
              {modulosEmOrdem.map((opcao) => (
                <option key={opcao} value={opcao}>
                  {rotuloDoModulo[opcao]}
                </option>
              ))}
            </select>
            {tentouEnviar && semModulo ? (
              <span className={estilos.erro}>Escolha o módulo.</span>
            ) : (
              <span className={estilos.ajuda}>Módulo do sistema ao qual a ação se relaciona.</span>
            )}
          </div>
        </div>
      </section>

      <section className={estilos.secao}>
        <h3 className={estilos.tituloDaSecao}>
          Clientes afetados <span className={estilos.obrigatorio}>*</span>
        </h3>

        <div className={estilos.campo}>
          <div
            className={estilos.listaDeClientes}
            role="group"
            aria-label="Clientes afetados"
            aria-invalid={tentouEnviar && semCliente}
          >
            {clientesAtivos.map((cliente) => (
              <label key={cliente.id} className={estilos.opcaoDeCliente}>
                <input
                  type="checkbox"
                  checked={clienteIds.includes(cliente.id)}
                  onChange={() => {
                    alternarCliente(cliente.id);
                  }}
                />
                {cliente.nome}
              </label>
            ))}
          </div>

          {tentouEnviar && semCliente ? (
            <span className={estilos.erro}>
              Escolha ao menos um cliente. Toda ação precisa de um cliente afetado.
            </span>
          ) : (
            <span className={estilos.contagem}>
              {clienteIds.length === 0
                ? 'Nenhum selecionado'
                : `${String(clienteIds.length)} selecionado${clienteIds.length === 1 ? '' : 's'}`}
            </span>
          )}
        </div>
      </section>

      {/* Rodapé colado no fim do formulário, e não flutuante: o corpo da gaveta já rola
          como um bloco, e barra fixa sobre um formulário de 640px comeria espaço útil. */}
      <div className={estilos.acoes}>
        <button className={estilos.botao} type="submit" disabled={criar.isPending || faltaCadastro}>
          {criar.isPending ? 'Criando...' : 'Criar rascunho'}
        </button>

        <button
          className={estilos.botaoSecundario}
          type="button"
          onClick={aoDesistir}
          disabled={criar.isPending}
        >
          Desistir
        </button>
      </div>
    </form>
  );
}
